// Cold-start mitigation for free-tier hosting (Render Hobby).
//
// Renders free instances sleep after ~15 min of idle. The first request after
// that wakes them, taking 20–40s. To keep the live interview UX usable on $0
// hosting we proactively wake the backend at moments when the user is about
// to need it but isn't waiting on a response yet:
//
//   * On app boot — before the auth-gated session list call
//   * When the new-session dialog opens — they're about to create a session
//   * On a coarse timer while the live session page is open
//
// All pings hit `GET /api/` which is public (no auth) and trivially cheap.
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HEALTH_URL = BACKEND_URL ? `${BACKEND_URL}/api/` : "/api/";

// Show a "waking up" notice if the first ping is slow. We only ever surface
// this once per page load so it doesn't become noise.
let warmedOnce = false;
let warmingToastShown = false;
let warmingPromise = null;

export function warmBackend({ showSlowNotice = true } = {}) {
    if (warmedOnce) return Promise.resolve("warm");
    if (warmingPromise) return warmingPromise;

    const started = Date.now();
    let slowTimer = null;
    if (showSlowNotice) {
        slowTimer = setTimeout(() => {
            if (warmingToastShown) return;
            warmingToastShown = true;
            toast.info("Waking the server… first load can take ~30s on free hosting.");
        }, 4000);
    }

    warmingPromise = fetch(HEALTH_URL, { method: "GET", credentials: "omit" })
        .then((r) => {
            warmedOnce = r.ok;
            return r.ok ? "warm" : "error";
        })
        .catch(() => "error")
        .finally(() => {
            if (slowTimer) clearTimeout(slowTimer);
            warmingPromise = null;
            const elapsed = Date.now() - started;
            // Help debugging: if the ping was unusually slow log it once.
            if (elapsed > 8000) {
                // eslint-disable-next-line no-console
                console.info(`[warmup] backend cold start: ${elapsed}ms`);
            }
        });

    return warmingPromise;
}
